import { ConsultantFormData } from '@/types/consultant-registration.types';

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

export function validateStep(step: number, formData: ConsultantFormData): ValidationResult {
  const errors: Record<string, string> = {};

  switch (step) {
    case 1:
      validateStepOne(formData, errors);
      break;
    case 2:
      validateStepTwo(formData, errors);
      break;
    case 3:
      validateStepThree(formData, errors);
      break;
    case 4:
      validateStepFour(formData, errors);
      break;
    default:
      break;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

function validateStepOne(formData: ConsultantFormData, errors: Record<string, string>) {
  // First Name
  if (!formData.firstName || formData.firstName.trim().length < 2) {
    errors.firstName = 'First name is required (min. 2 characters)';
  }

  // Last Name
  if (!formData.lastName || formData.lastName.trim().length < 2) {
    errors.lastName = 'Last name is required (min. 2 characters)';
  }

  // Email
  if (!formData.email || !isValidEmail(formData.email)) {
    errors.email = 'A valid email address is required';
  }

  // Phone
  if (!formData.phone || formData.phone.trim().length < 7) {
    errors.phone = 'Phone number is required (min. 7 characters)';
  }

  // Password
  if (!formData.password || formData.password.length < 6) {
    errors.password = 'Password must be at least 6 characters';
  }

  // Confirm Password
  if (!formData.confirmPassword) {
    errors.confirmPassword = 'Please confirm your password';
  } else if (formData.password !== formData.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }

  // Institution Types
  if (!formData.institutionTypes || formData.institutionTypes.length === 0) {
    errors.institutionTypes = 'Please select at least one institution type';
  }

  // How Heard
  if (!formData.howHeard || formData.howHeard.trim().length < 2) {
    errors.howHeard = 'Please tell us how you heard about AcquizGroup';
  }

  // Mother Tongue
  if (!formData.motherTongue) {
    errors.motherTongue = 'Please select your mother tongue';
  }

  // Address
  if (!formData.streetAddress || formData.streetAddress.trim().length < 5) {
    errors.streetAddress = 'Street address is required (use the search above)';
  }

  if (!formData.city || formData.city.trim().length < 2) {
    errors.city = 'City is required';
  }

  if (!formData.stateProvince || formData.stateProvince.trim().length < 2) {
    errors.stateProvince = 'State/Province is required';
  }

  if (!formData.zipPostalCode || formData.zipPostalCode.trim().length < 3) {
    errors.zipPostalCode = 'Zip/Postal code is required';
  }

  if (!formData.country || formData.country.trim().length < 2) {
    errors.country = 'Country is required';
  }
}

function validateStepTwo(formData: ConsultantFormData, errors: Record<string, string>) {
  // Specializations
  if (!formData.specializations || formData.specializations.length === 0) {
    errors.specializations = 'Please add at least one business sector';
  } else {
    formData.specializations.forEach((spec, index) => {
      if (!spec.industry) {
        errors[`spec${index}_industry`] = `Sector ${index + 1}: Please select an industry`;
      }
      if (!spec.expertises || spec.expertises.length === 0) {
        errors[`spec${index}_expertises`] = `Sector ${index + 1}: Please select at least one expertise`;
      }
      if (!spec.experienceYears) {
        errors[`spec${index}_experience`] = `Sector ${index + 1}: Please select years of experience`;
      }
    });
  }

  // Licenses (if applicable)
  if (formData.hasLicense === 'yes') {
    if (!formData.licenses || formData.licenses.length === 0) {
      errors.licenses = 'Please add at least one license/permit';
    } else {
      formData.licenses.forEach((license, index) => {
        if (!license.details || license.details.trim().length === 0) {
          errors[`license${index}_details`] = `License ${index + 1}: Details are required`;
        }
        if (!license.number || license.number.trim().length === 0) {
          errors[`license${index}_number`] = `License ${index + 1}: Number is required`;
        }
      });
    }
  }
}

function validateStepThree(formData: ConsultantFormData, errors: Record<string, string>) {
  // Services
  if (!formData.services || formData.services.trim().length < 5) {
    errors.services = 'Please describe your services (min. 5 characters)';
  }

  // Compensation Options
  if (!formData.compensationOptions || formData.compensationOptions.length === 0) {
    errors.compensationOptions = 'Please select at least one compensation option';
  }

  // Currencies
  if (!formData.currencies || formData.currencies.length === 0) {
    errors.currencies = 'Please select at least one currency';
  }

  // Hourly Rate validation
  if (formData.compensationOptions.includes('per_hour')) {
    if (!formData.hourlyRate || formData.hourlyRate.trim().length === 0) {
      errors.hourlyRate = 'Hourly rate is required';
    }
    if (!formData.hourlyRateCurrency) {
      errors.hourlyRateCurrency = 'Please select the hourly rate currency';
    }
  }

  // Equity Partnership validation
  if (formData.compensationOptions.includes('equity_partnership')) {
    if (!formData.equityPartnershipDetails) {
      errors.equityPartnership = 'Please indicate your willingness for equity partnership (Yes/No)';
    }
  }

  // Availability
  if (!formData.availability) {
    errors.availability = 'Please select your availability';
  }
}

function validateStepFour(formData: ConsultantFormData, errors: Record<string, string>) {
  // Terms Accepted
  if (!formData.termsAccepted) {
    errors.termsAccepted = 'You must accept the Terms and Conditions';
  }

  // Background Check Authorization
  if (!formData.backgroundCheckAuth) {
    errors.backgroundCheckAuth = 'You must authorize the background check';
  }
}

function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function scrollToFirstError() {
  setTimeout(() => {
    const firstError = document.querySelector('[data-error="true"]');
    if (firstError) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, 100);
}
